const VALID_TYPES = ['comment_added', 'status_changed', 'inquiry_assigned', 'document_converted'];
const VALID_ENTITY_TYPES = ['Inquiry', 'Quote', 'Offer', 'Order'];

const PROCUREMENT_ROLES = ['procurement manager', 'procurement user'];
const SALES_ROLES = ['admin', 'sales manager', 'sales user'];
const INQUIRY_QUOTE_COMMENT_ROLES = [...PROCUREMENT_ROLES, ...SALES_ROLES];

export const getNow = () => new Date().toISOString().slice(0, 16).replace('T', ' ');

const usersByRoles = (users = [], roles) => users.filter((user) => user.active && roles.includes(user.role));

/**
 * Builds one notification record per distinct, non-excluded target user.
 * Returns [] if `type`/`entityType` is invalid — this is the single
 * validation gate every notification passes through (Requirement 1.4).
 */
export const createNotifications = ({
  type,
  entityType,
  entityId,
  entityLabel,
  message,
  targetUsers = [],
  excludeUserId,
  currentMaxId = 0,
}) => {
  if (!VALID_TYPES.includes(type) || !VALID_ENTITY_TYPES.includes(entityType)) return [];

  const seenIds = new Set();
  const recipients = targetUsers.filter((user) => {
    if (!user || user.id === excludeUserId || seenIds.has(user.id)) return false;
    seenIds.add(user.id);
    return true;
  });

  return recipients.map((user, index) => ({
    id: currentMaxId + index + 1,
    targetUserId: user.id,
    type,
    entityType,
    entityId,
    entityLabel: String(entityLabel ?? '').slice(0, 100),
    message,
    timestamp: getNow(),
    read: false,
  }));
};

export const createCommentNotifications = ({
  entityType, entityId, entityLabel, authorId, authorName, users = [], currentMaxId,
}) => createNotifications({
  type: 'comment_added',
  entityType,
  entityId,
  entityLabel,
  message: `${authorName} left a comment on ${entityLabel}`,
  targetUsers: entityType === 'Offer' || entityType === 'Order'
    ? users.filter((user) => user.active)
    : usersByRoles(users, INQUIRY_QUOTE_COMMENT_ROLES),
  excludeUserId: authorId,
  currentMaxId,
});

export const createInquiryAssignedNotifications = ({
  entityId, entityLabel, creatorId, users, currentMaxId,
}) => createNotifications({
  type: 'inquiry_assigned',
  entityType: 'Inquiry',
  entityId,
  entityLabel,
  message: `New inquiry ${entityId} has been assigned to Procurement`,
  targetUsers: usersByRoles(users, PROCUREMENT_ROLES),
  excludeUserId: creatorId,
  currentMaxId,
});

/**
 * Status-change notifications. Only the (entityType, newStatus) combinations
 * listed in the design doc's targeting table produce a notification —
 * anything else (e.g. an Inquiry status change that isn't 'Sent to
 * Procurement') is a valid no-op and returns [].
 */
export const createStatusChangeNotifications = ({
  entityType, entityId, entityLabel, newStatus, actorId, users, currentMaxId,
}) => {
  let roles;
  let message;

  if (entityType === 'Inquiry' && newStatus === 'Sent to Procurement') {
    roles = PROCUREMENT_ROLES;
    message = `Inquiry ${entityId} has been sent to Procurement`;
  } else if (entityType === 'Quote' && newStatus === 'Sent to Sales Quote') {
    roles = SALES_ROLES;
    message = `Quote ${entityId} is ready — an Offer can now be created`;
  } else if (entityType === 'Quote' && newStatus === 'Ready for Procurement') {
    roles = PROCUREMENT_ROLES;
    message = `Quote ${entityId} has been returned to Procurement`;
  } else if (entityType === 'Offer') {
    roles = SALES_ROLES;
    message = `Offer ${entityId} status changed to ${newStatus}`;
  } else if (entityType === 'Order') {
    roles = SALES_ROLES;
    message = `Order ${entityId} status changed to ${newStatus}`;
  } else {
    return [];
  }

  return createNotifications({
    type: 'status_changed',
    entityType,
    entityId,
    entityLabel,
    message,
    targetUsers: usersByRoles(users, roles),
    excludeUserId: actorId,
    currentMaxId,
  });
};

/**
 * One notification per Quote produced by "Create Quotes" on an Inquiry —
 * tells Procurement the quote is ready to be filled out with supplier
 * pricing.
 */
export const createQuotesCreatedNotifications = ({
  inquiryId, quoteIds = [], actorId, users, currentMaxId,
}) => quoteIds.reduce((acc, quoteId, index) => [
  ...acc,
  ...createNotifications({
    type: 'document_converted',
    entityType: 'Quote',
    entityId: quoteId,
    entityLabel: quoteId,
    message: `Quote ${quoteId} was created from Inquiry ${inquiryId} and is ready to be filled out with supplier pricing`,
    targetUsers: usersByRoles(users, PROCUREMENT_ROLES),
    excludeUserId: actorId,
    currentMaxId: currentMaxId + acc.length,
  }),
], []);

export const createDocumentConvertedNotifications = ({
  fromType, fromId, toType, toId, actorId, users, currentMaxId,
}) => createNotifications({
  type: 'document_converted',
  entityType: toType,
  entityId: toId,
  entityLabel: toId,
  message: `${fromType} ${fromId} has been converted to ${toType} ${toId}`,
  targetUsers: usersByRoles(users, SALES_ROLES),
  excludeUserId: actorId,
  currentMaxId,
});
